import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import FileDelimitedIcon from 'mdi-react/FileDelimitedIcon'
import MicrosoftExcelIcon from 'mdi-react/MicrosoftExcelIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsThreeStepIT } from '@/hooks/access'
import queries from '@/modules/admin/queries'

import {
  getOnOffboardingColumnFormats,
  getOnOffboardingExportRows,
} from '../onOffboardingReportHelpers'

import { exportDataToExcel } from '../../helpers'
import type { HrOffboardingReport, OnboardingReport, ReportFilters } from '../../resources'
import adminResources from '../../resources'

type Props = {
  title: string
  filters: ReportFilters
  type: 'onboarding' | 'offboarding'
}

export const OnOffboardingOptions: FC<Props> = ({ title, filters, type }) => {
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()
  const isOnboarding = type === 'onboarding'
  const queryKey = isOnboarding
    ? queries.adminOnboardingReports(filters)
    : queries.adminHrOffboardingReports(filters)

  const { data: reports } = useQuery<(OnboardingReport | HrOffboardingReport)[]>(queryKey, () =>
    isOnboarding
      ? adminResources.getOnboardingReports(filters)
      : adminResources.getHrOffboardingReports(filters)
  )

  if (!reports) throw Error('We have encountered a problem while loading reports')

  const reportsData = getOnOffboardingExportRows(
    reports as (OnboardingReport | HrOffboardingReport)[],
    type
  )
  const columnFormats = getOnOffboardingColumnFormats(type)

  return (
    <div className="grid grid-cols-2">
      <Button
        Icon={<MicrosoftExcelIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="Excel" />
      </Button>
      <Button
        Icon={<FileDelimitedIcon className={twJoin(!isThreeStepIT && 'text-emerald-600')} />}
        className="mb-4 ml-0"
        onClick={() =>
          exportDataToExcel(reportsData, title, {
            translate,
            columnFormats,
            extension: 'csv',
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          })
        }
      >
        <Translation id="CSV" />
      </Button>
    </div>
  )
}
